
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import OnboardingForm from "./OnboardingForm";
import { Pencil } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type OnboardingData = Database['public']['Tables']['onboarding_data']['Row'];

const OnboardingTab = () => {
  const { user } = useAuth();
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    const fetchOnboardingData = async () => {
      if (user?.email) {
        setIsLoading(true);
        try {
          const { data, error } = await supabase
            .from('onboarding_data')
            .select('*')
            .eq('user_id', user.email)
            .maybeSingle();

          if (error && error.code !== 'PGRST116') {
            console.error("Error fetching onboarding data:", error);
            throw error;
          }

          setOnboardingData(data || null);
        } catch (error) {
          console.error("Error fetching onboarding data:", error);
          toast.error("Fehler beim Laden der Onboarding-Daten");
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchOnboardingData();
  }, [user, formOpen]);

  const handleEditOnboarding = () => {
    setFormOpen(true);
  };

  const renderOnboardingStatus = () => {
    if (isLoading) {
      return <div className="text-center py-8">Daten werden geladen...</div>;
    }

    if (!onboardingData || !onboardingData.has_completed_onboarding) {
      return (
        <div className="text-center py-8">
          <p className="mb-4 text-muted-foreground">Sie haben das Onboarding noch nicht abgeschlossen.</p>
          <Button onClick={handleEditOnboarding}>Onboarding jetzt abschließen</Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">Onboarding-Status</h3>
          <Button variant="outline" onClick={handleEditOnboarding}>
            <Pencil className="mr-2 h-4 w-4" />
            Bearbeiten
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {onboardingData.company_info && (
            <div>
              <h4 className="font-medium mb-2">Unternehmensinformationen</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{onboardingData.company_info}</p>
            </div>
          )}

          {onboardingData.address && (
            <div>
              <h4 className="font-medium mb-2">Adresse</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{onboardingData.address}</p>
            </div>
          )}

          {onboardingData.domain && (
            <div>
              <h4 className="font-medium mb-2">Domain</h4>
              <p className="text-sm text-muted-foreground">{onboardingData.domain}</p>
            </div>
          )}

          {onboardingData.tax_id && (
            <div>
              <h4 className="font-medium mb-2">Steuernummer</h4>
              <p className="text-sm text-muted-foreground">{onboardingData.tax_id}</p>
            </div>
          )}

          {onboardingData.payment_options && onboardingData.payment_options.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Zahlungsoptionen</h4>
              <p className="text-sm text-muted-foreground">{onboardingData.payment_options.join(', ')}</p>
            </div>
          )}

          {onboardingData.target_audience && (
            <div>
              <h4 className="font-medium mb-2">Zielgruppe</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{onboardingData.target_audience}</p>
            </div>
          )}

          {onboardingData.ideal_customer && (
            <div>
              <h4 className="font-medium mb-2">Idealer Kunde</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{onboardingData.ideal_customer}</p>
            </div>
          )}

          {onboardingData.qualification_questions && (
            <div>
              <h4 className="font-medium mb-2">Qualifikationsfragen</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{onboardingData.qualification_questions}</p>
            </div>
          )}

          {onboardingData.color_scheme && (
            <div>
              <h4 className="font-medium mb-2">Farbschema</h4>
              <p className="text-sm text-muted-foreground">{onboardingData.color_scheme}</p>
            </div>
          )}

          {onboardingData.website_examples && (
            <div>
              <h4 className="font-medium mb-2">Beispiel-Webseiten</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{onboardingData.website_examples}</p>
            </div>
          )}

          {onboardingData.competitor_websites && (
            <div>
              <h4 className="font-medium mb-2">Wettbewerber-Webseiten</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{onboardingData.competitor_websites}</p>
            </div>
          )}

          {onboardingData.faq && onboardingData.faq.length > 0 && (
            <div className="col-span-1 md:col-span-2">
              <h4 className="font-medium mb-2">FAQ</h4>
              <div className="space-y-4">
                {onboardingData.faq.map((item, index) => (
                  <div key={index} className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Onboarding-Informationen</CardTitle>
          <CardDescription>
            Ihre Onboarding-Informationen zu Ihrer Firma und Zielgruppe
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderOnboardingStatus()}
        </CardContent>
      </Card>

      <OnboardingForm
        isOpen={formOpen}
        onOpenChange={setFormOpen}
        initialData={onboardingData}
        userId={user?.email}
      />
    </>
  );
};

export default OnboardingTab;
